import { isApiProblem } from "../../shared/api/problem";
import { userLabel } from "../../shared/lib/personLabel";
import { emergencyText } from "../../shared/i18n/emergencyText";
import { countGraphemes } from "../../shared/lib/graphemes";
import { formatTime } from "../../shared/lib/format";
import type { Session } from "../../shared/session/session-context";
import type { BypassedClause, EmergencyApproverPreview, EmergencyBypassGrant, EmergencyBypassState, EmergencyOtpRequest, EmergencyRefusal } from "./emergencyApi";

/** Contract limits (design §1.2, §5): a reason is 10–500 grapheme clusters after trim; a code is exactly 6 ASCII digits. */
export const REASON_MIN = 10;
export const REASON_MAX = 500;
export const CODE_LENGTH = 6;

/**
 * Whether this person could ever see the emergency controls (design §6, §8): the flag on, a TFG staff account that is not an AI
 * agent, and not in test mode. A first gate only, so nothing is fetched for anyone else; the server's `available` decides.
 * Partners never get a hint the feature exists.
 */
export function emergencyEligible(session: Pick<Session, "flag" | "isPartner" | "testMode" | "user">): boolean {
  return session.flag("EmergencyOtp") && !session.isPartner && session.testMode?.active !== true && session.user.isAgent !== true && session.user.userKind === "internal";
}

/** What the card's ⋯ entry offers this person (design §6.1): request, enter the code of a pending request, or end their bypass. */
export type EmergencyMenuMode = "request" | "enter" | "end";

export function menuMode(state: EmergencyBypassState | undefined): EmergencyMenuMode | null {
  if (!state) return null;
  if (state.myGrant && state.myGrant.endedAt == null) return "end";
  if (!state.available) return null;
  if (state.myRequest && state.myRequest.status === "pending") return "enter";
  return "request";
}

/** The `Problem.emergencyRefusal` of a refused call, when it is one. */
export function refusalOf(error: unknown): EmergencyRefusal | null {
  if (!isApiProblem(error)) return null;
  const refusal = (error.problem as { emergencyRefusal?: unknown } | undefined)?.emergencyRefusal;
  return typeof refusal === "string" ? (refusal as EmergencyRefusal) : null;
}

/** `Problem.attemptsLeft` on a wrong code, when present. */
export function attemptsLeftOf(error: unknown): number | null {
  if (!isApiProblem(error)) return null;
  const left = (error.problem as { attemptsLeft?: unknown } | undefined)?.attemptsLeft;
  return typeof left === "number" ? left : null;
}

/** Words for a refusal the dialogs show; null when the generic problem message is better. */
export function refusalWords(refusal: EmergencyRefusal | null): string | null {
  switch (refusal) {
    case "staff_only":
      return emergencyText("staffOnly");
    case "test_mode":
      return emergencyText("testMode");
    case "not_configured":
      return emergencyText("notConfigured");
    case "no_approver":
      return emergencyText("noApprover");
    case "already_active":
      return emergencyText("alreadyActive");
    case "expired":
      return emergencyText("expired");
    case "locked":
      return emergencyText("locked");
    case "not_pending":
      return emergencyText("notPending");
    case "not_active":
      return emergencyText("notActive");
    default:
      return null;
  }
}

/** 429 on a request: "You can request again at 14:32." from Retry-After, when the server sent it. */
export function rateLimitWords(error: unknown, now: number = Date.now()): string | null {
  if (!isApiProblem(error) || error.status !== 429) return null;
  const seconds = error.retryAfterSeconds;
  return seconds === undefined ? emergencyText("rateLimitedNoTime") : emergencyText("rateLimited", { time: formatTime(new Date(now + seconds * 1000)) });
}

/** A reason's problem in words, or null when it is fine. */
export function reasonProblem(reason: string): string | null {
  const n = countGraphemes(reason.trim());
  if (n < REASON_MIN) return emergencyText("reasonShort", { n });
  if (n > REASON_MAX) return emergencyText("reasonLong", { n });
  return null;
}

/** Digits only, at most six: what the code box keeps from typing or pasting ("482 913" → "482913"). */
export function normaliseCode(input: string): string {
  return input.replace(/\D/g, "").slice(0, CODE_LENGTH);
}

/** Whole seconds until `expiresAt` (never negative). */
export function secondsLeft(expiresAt: string, now: number = Date.now()): number {
  const at = Date.parse(expiresAt);
  return Number.isNaN(at) ? 0 : Math.max(0, Math.ceil((at - now) / 1000));
}

/** "9:41" for the countdown. */
export function countdown(seconds: number): string {
  const s = Math.max(0, Math.floor(seconds));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
}

/** Where the code went (design §1.5), for the requester: names are shown here (they must call them), never in a notification. */
export function deliveredWords(request: Pick<EmergencyOtpRequest, "basis" | "approvers">, projectKey: string): string {
  if (request.basis === "self") return emergencyText("sentSelf");
  const names = request.approvers.map((a) => a.user.displayName).join(", ");
  const telegram = request.approvers.some((a) => a.deliveredVia.includes("telegram"));
  const channel = emergencyText(telegram ? "channelBoth" : "channelInApp");
  return request.basis === "fallback" ? emergencyText("sentFallback", { names, channel }) : emergencyText("sentProjectDirector", { names, channel, project: projectKey });
}

/**
 * Batch 18 (§11.6): "Who gets the code" before sending, from `getEmergencyOtpApprovers`: each approver by name and crew label
 * with the channel the code would take ("by Telegram and the bell" / "by the bell only (Telegram is not linked)"), or the
 * self-approver sentence. Null when the preview names nobody, so the dialog keeps its generic sentence.
 */
export function approverPreviewWords(preview: Pick<EmergencyApproverPreview, "basis" | "approvers">): string | null {
  if (preview.basis === "self") return emergencyText("requestWhoSelf");
  if (preview.approvers.length === 0) return null;
  const people = preview.approvers.map((approver) =>
    emergencyText("requestWhoPerson", {
      name: approver.user.displayName,
      role: approver.roleLabel,
      channel: emergencyText(approver.telegramLinked ? "whoChannelBoth" : "whoChannelBell"),
    }),
  );
  const list = people.length === 1 ? people[0]! : `${people.slice(0, -1).join(", ")}${emergencyText("requestWhoAnd")}${people[people.length - 1]!}`;
  return emergencyText("requestWhoNamed", { list });
}

/**
 * A permission code (`defect.triage`) or a rule kind (`segregation_of_duties`): never shown to a person (batch 18, GRF-54 §5.10).
 * An older server's `detail` named the skipped who by its codes ("workitem.transition.basic (assignee)").
 */
export const RAW_CODE = /[a-z]+\.[a-z_]+|[a-z]+_[a-z_]+/;

/** Batch 18 (GRF-54 §5.10): the words when the server's detail is missing or names a code. */
export const SKIPPED_WHO_WORDS = "who may make this move";
export const SKIPPED_RULE_WORDS = "a rule of this workflow";

/**
 * The skipped clauses in words (design §6.4 "This move skips: …"): the server's own `detail` (with `Features__MoveReasons` it
 * names roles by crew label and permissions by their Settings → Permissions label), else — no detail, or one that still carries a
 * code — "who may make this move" for the `who` clause and "a rule of this workflow" for any other. Never a code (§5.10).
 */
export function clauseWords(clauses: readonly Pick<BypassedClause, "rule" | "detail">[]): string[] {
  const words = clauses.map((clause) => {
    const detail = clause.detail.trim();
    if (detail && !RAW_CODE.test(detail)) return detail;
    return clause.rule === "who" ? SKIPPED_WHO_WORDS : SKIPPED_RULE_WORDS;
  });
  return [...new Set(words.filter(Boolean))];
}

/** An active grant (contract: `endedAt` null while active). */
export function isActiveGrant(grant: EmergencyBypassGrant | null | undefined): grant is EmergencyBypassGrant {
  return !!grant && (grant.endedAt === null || grant.endedAt === undefined);
}

/**
 * Who sees "End emergency bypass" on a grant (design §6.4, ruling Q3): its holder, the approver whose code was used, a Chief
 * Pilot of the project, or a System Admin (never in test mode). Advisory: the server decides and refuses anyone else.
 */
export function canEndGrant(grant: EmergencyBypassGrant, session: Pick<Session, "user" | "isSystemAdmin" | "rolesIn" | "testMode">, projectKey: string): boolean {
  if (session.testMode?.active) return false;
  const me = session.user.id;
  return grant.holder.id === me || grant.issuedBy.id === me || session.isSystemAdmin || session.rolesIn(projectKey).includes("ProjectDirector");
}

/** The holder ends without a note; anyone else needs one of 10–500 characters (contract `EmergencyBypassEnd`). */
export function endNoteProblem(note: string, holder: boolean): string | null {
  const n = countGraphemes(note.trim());
  if (holder && n === 0) return null;
  if (n < REASON_MIN) return emergencyText("endNoteShort", { n });
  if (n > REASON_MAX) return emergencyText("reasonLong", { n });
  return null;
}

/** The code shown large, as two groups of three ("482 913"), and spelled out digit by digit for a screen reader. */
export function codeGroups(code: string): { visual: string; spoken: string } {
  return { visual: code.length === 6 ? `${code.slice(0, 3)} ${code.slice(3)}` : code, spoken: code.split("").join(" ") };
}

/** Minutes left on a request notified at `createdAt`: every code lives exactly 10 minutes (design §1.4, a constant). */
export const CODE_LIFETIME_MS = 10 * 60 * 1000;
export function minutesLeftFrom(createdAt: string, now: number = Date.now()): number {
  const at = Date.parse(createdAt);
  if (Number.isNaN(at)) return 0;
  return Math.max(0, Math.ceil((at + CODE_LIFETIME_MS - now) / 60000));
}

/** A person who may be the fallback approver (ruling Q1): an active internal System Admin who is not an AI agent. */
export interface FallbackCandidate {
  id: number;
  label: string;
}

/**
 * The candidates, from the active `system_admin` grants joined with the user directory: active, internal, not an AI agent.
 * Pure, so the rule is tested on its own. The server validates again (422 naming the field).
 */
export function fallbackCandidates(
  grants: readonly { user: { id: number; displayName: string }; role: string; revokedAt?: string | null }[],
  directory: ReadonlyMap<number, { isActive: boolean; userKind: string; isAgent?: boolean; displayName: string; profileName?: string | null; label?: string }>,
): FallbackCandidate[] {
  const seen = new Set<number>();
  const out: FallbackCandidate[] = [];
  for (const grant of grants) {
    if (grant.role !== "system_admin" || grant.revokedAt || seen.has(grant.user.id)) continue;
    const person = directory.get(grant.user.id);
    if (!person || !person.isActive || person.userKind !== "internal" || person.isAgent === true) continue;
    seen.add(grant.user.id);
    out.push({ id: grant.user.id, label: userLabel(person) });
  }
  return out.sort((a, b) => a.label.localeCompare(b.label));
}
